'use client';

import { useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import { FeedbackTab } from './FeedbackTab';
import { FaqTab } from './FaqTab';

type Tab = 'feedback' | 'faq';

export function FeedbackWidget({ pageSlug }: { pageSlug: string }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('feedback');
  const { user, loading, signIn, signOut } = useAuth();
  const dialogRef = useRef<HTMLDivElement>(null);
  const lang = useCalendarStore((s) => s.lang);
  useFocusTrap(dialogRef, open);

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-brand text-text-on-brand shadow-lg transition-transform hover:scale-105 active:scale-95"
        aria-label={uiFb('ui.open_feedback', 'Open feedback', lang)}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {/* Modal */}
      {open && (
        <div
          ref={dialogRef}
          className="fixed inset-0 z-[100] flex items-end justify-end p-4 sm:items-center sm:justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={uiFb('ui.feedback_faq', 'Feedback & FAQ', lang)}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-[var(--overlay-scrim)] backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <div className="relative w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden rounded-2xl border border-border-primary bg-surface-sidebar shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border-subtle px-4 pt-4 pb-3">
              <h2 className="font-heading text-lg font-bold text-text-primary">{uiFb('ui.feedback_faq', 'Feedback & FAQ', lang)}</h2>
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg p-1 text-muted transition-colors hover:bg-surface-hover hover:text-text-secondary"
                aria-label={uiFb('ui.close_menu', 'Close menu', lang)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border-subtle" role="tablist">
              {(['feedback', 'faq'] as const).map((t) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={tab === t}
                  onClick={() => setTab(t)}
                  className={`flex-1 py-2.5 text-center text-sm font-medium transition-colors ${
                    tab === t
                      ? 'border-b-2 border-brand text-text-primary'
                      : 'text-muted hover:text-text-secondary'
                  }`}
                >
                  {t === 'feedback' ? uiFb('ui.feedback', 'Feedback', lang) : uiFb('ui.faq', 'FAQ', lang)}
                </button>
              ))}
            </div>

            {/* Auth bar — below tabs so it doesn't block anonymous interactions */}
            <div className="flex items-center gap-2 border-b border-border-subtle px-4 py-2">
              {loading ? (
                <span className="text-xs text-muted">{uiFb('ui.loading', 'Loading...', lang)}</span>
              ) : user ? (
                <>
                  {user.user_metadata?.avatar_url && (
                    <img src={user.user_metadata.avatar_url} alt="" className="h-6 w-6 rounded-full" />
                  )}
                  <span className="flex-1 truncate text-xs text-text-secondary">
                    {user.user_metadata?.full_name ?? user.email}
                  </span>
                  <button
                    onClick={() => signOut()}
                    className="text-xs text-muted hover:text-text-secondary"
                  >
                    {uiFb('ui.sign_out', 'Sign out', lang)}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => signIn()}
                  className="flex items-center gap-2 content-card px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-hover"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {uiFb('ui.sign_in_google', 'Sign in with Google', lang)}
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {tab === 'feedback' ? (
                <FeedbackTab pageSlug={pageSlug} user={user} />
              ) : (
                <FaqTab pageSlug={pageSlug} user={user} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
